// Single source of truth for template-style → CSS conversion (RES-561 compare
// layout parity, plan-compareDocuments.md §3.12 A option 1B).
//
// Template styles store dimensions as bare numbers meaning POINTS (the PDF
// engine's unit), except lineHeight where a value < 2 is a unitless multiplier
// (the CSS idiom the PDF renderer also uses). Every consumer — the editor's
// DataController and the Raw slate wrappers the compare/version views render
// through — must share this one conversion: the private copies this replaces
// had drifted, and one of them turned `lineHeight: 1.2` into `1.2pt` (≈1.6px),
// collapsing every list on the compare page.
/**
 * Rich text's line height: the style's own lineHeight when the style applies it (applyLineHeight, as the
 * PDF's EditableText does), otherwise `fallback` (rich text has always shown at 1.2, as the PDF draws it).
 */
export function richTextLineHeight(templateStyle, converted, fallback) {
	if (templateStyle && templateStyle.applyLineHeight && converted && converted.lineHeight) {
		return converted.lineHeight;
	}
	return fallback;
}

export function getStyle(style, includeCellStyles) {
	if (!style) {
		console.error("No style provided to getStyle, returning empty object", style);
		return {};
	}
	// applyLineHeight is a PDF instruction (lay lines out at lineHeight), not CSS
	// eslint-disable-next-line no-unused-vars
	const { size, weight, paddingBelowParagraph, height, lineHeight, indent, minHeight, paddingLeft, paddingRight, paddingBottom, width, borderBottomWidth, borderTopWidth, paddingInlineStart, marginBlockStart, marginBlockEnd, applyLineHeight, ...others } = style;

	const theStyle = { ...others };

	const ptIfNumber = (v) => (typeof v === "number" ? v + "pt" : v);
	if (paddingInlineStart !== undefined) theStyle.paddingInlineStart = ptIfNumber(paddingInlineStart);
	if (marginBlockStart !== undefined) theStyle.marginBlockStart = ptIfNumber(marginBlockStart);
	if (marginBlockEnd !== undefined) theStyle.marginBlockEnd = ptIfNumber(marginBlockEnd);

	if (theStyle.borderLeftWidth) {
		theStyle.borderLeftWidth += "pt";
	}
	if (theStyle.borderRightWidth) {
		theStyle.borderRightWidth += "pt";
	}
	// Converted unconditionally like the left/right widths (review finding: the
	// old Raw copies passed borderTopWidth through for non-cell styles — e.g. a
	// tableBorder style — and the PDF draws it; gating it on includeCellStyles
	// would silently drop that border on the compare page).
	if (borderTopWidth) {
		theStyle.borderTopWidth = borderTopWidth + "pt";
	}
	if (theStyle.paddingTop) {
		theStyle.paddingTop += "pt";
	}

	if (size) {
		theStyle.fontSize = size + "pt";
	}
	if (weight) {
		theStyle.fontWeight = weight;
	}
	if (paddingBelowParagraph) {
		theStyle.marginBottom = paddingBelowParagraph + "pt";
	}
	if (width) {
		theStyle.width = width + "pt";
	}
	if (lineHeight) {
		theStyle.lineHeight = lineHeight < 2 ? lineHeight : lineHeight + "pt";
	}

	if (indent) {
		theStyle.indent = indent;
	}
	if (includeCellStyles) {
		if (minHeight) {
			theStyle.minHeight = minHeight + "pt";
		}
		if (height) {
			theStyle.height = height + "pt";
		}
		if (paddingLeft) {
			theStyle.paddingLeft = paddingLeft + "pt";
		}
		if (paddingRight) {
			theStyle.paddingRight = paddingRight + "pt";
		}
		if (paddingBottom) {
			theStyle.paddingBottom = paddingBottom + "pt";
		}
		if (borderBottomWidth) {
			theStyle.borderBottomWidth = borderBottomWidth + "pt";
		}
		if (lineHeight) {
			theStyle.lineHeight = lineHeight + (lineHeight < 2 ? "" : "pt");
		}
	}
	return theStyle;
}
